'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';

interface SearchResult {
  projectCode: string;
  resourceGuid: string;
  project: string;
  originator: string;
  volume: string;
  level: string;
  documentType: string;
  role: string;
  classification: string;
  documentNumber: number;
  suitabilityStatus: string;
  revision: string;
  fileName: string | null;
  fileExtension: string | null;
  folderPath: string | null;
}

interface SearchResultItemProps {
  result: SearchResult;
  onNavigateToResource?: () => void;
}

export default function SearchResultItem({ result, onNavigateToResource }: SearchResultItemProps) {
  const router = useRouter();
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const [thumbnailError, setThumbnailError] = useState(false);

  const getFileIcon = (extension: string | null) => {
    if (!extension) {
      return (
        <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
          <svg className="w-6 h-6 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
          </svg>
        </div>
      );
    }

    const ext = extension.toLowerCase().replace('.', '');

    // PDF files
    if (ext === 'pdf') {
      return (
        <div className="w-12 h-12 bg-red-100 dark:bg-red-900 rounded flex items-center justify-center">
          <span className="text-xs font-bold text-red-600 dark:text-red-300">PDF</span>
        </div>
      );
    }

    // Image files
    if (["jpg", "jpeg", "gif", "bmp", "png", "heic", "heif"].includes(ext)) {
      return (
        <div className="w-12 h-12 rounded flex items-center justify-center">
          {!thumbnailLoaded && !thumbnailError && (
            <svg className="w-4 h-4 animate-spin text-gray-400" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
            </svg>
          )}
          {thumbnailError && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100 rounded">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-4 h-4 text-gray-400">
                <path fill="currentColor" d="M21 19V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2ZM8.75 9.75A1.75 1.75 0 1 1 7 8a1.75 1.75 0 0 1 1.75 1.75ZM6 18l4.5-6 3 4 2-2.667L18 18H6Z" />
              </svg>
            </div>
          )}
          <img
            src={`/api/document/download-thumbnail?projectCode=${encodeURIComponent(result.projectCode)}&guid=${encodeURIComponent(result.resourceGuid)}`}
            loading="lazy"
            decoding="async"
            className={`w-10 h-10 ${thumbnailLoaded && !thumbnailError ? '' : 'invisible'}`}
            style={{ objectFit: 'cover' }}
            onLoad={() => setThumbnailLoaded(true)}
            onError={() => setThumbnailError(true)}
          />
        </div>
      );
    }

    // Excel files
    if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return (
        <div className="w-12 h-12 bg-green-100 dark:bg-green-900 rounded flex items-center justify-center">
          <span className="text-xs font-bold text-green-600 dark:text-green-300">XLS</span>
        </div>
      );
    }

    // Word files
    if (['doc', 'docx'].includes(ext)) {
      return (
        <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded flex items-center justify-center">
          <span className="text-xs font-bold text-blue-600 dark:text-blue-300">DOC</span>
        </div>
      );
    }

    // AutoCAD files
    if (['dwg', 'dxf'].includes(ext)) {
      return (
        <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900 rounded flex items-center justify-center">
          <span className="text-xs font-bold text-purple-600 dark:text-purple-300">CAD</span>
        </div>
      );
    }

    // Default file icon
    return (
      <div className="w-12 h-12 bg-gray-200 dark:bg-gray-600 rounded flex items-center justify-center">
        <span className="text-xs font-bold text-gray-600 dark:text-gray-300 uppercase">
          {ext.substring(0, 3)}
        </span>
      </div>
    );
  };

  const handleViewClick = () => {
    onNavigateToResource?.();
    router.push(`/project/${result.projectCode}/resource/${result.resourceGuid}`);
  };

  return (
    <div className="border rounded-lg p-4 bg-gray-50 dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors">
      <div className="flex items-start gap-4">
        {/* File Preview/Icon */}
        <div className="flex-shrink-0">
          {getFileIcon(result.fileExtension)}
        </div>

        {/* File Information */}
        <div className="flex-1 min-w-0 space-y-2">
          {/* File name */}
          <div className="font-medium text-sm truncate">
            {result.fileName}{result.fileExtension}
          </div>

          {/* ISO Code */}
          <div className="text-xs text-gray-600 dark:text-gray-300 truncate">
            ISO: {result.project}-{result.originator}-{result.volume}-{result.level}-{result.documentType}-{result.role}-{result.documentNumber}-{result.suitabilityStatus}-{result.revision}
          </div>

          {/* File path if available
          {result.folderPath && (
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
              Path: {result.folderPath}
            </div>
          )} */}
        </div>

        {/* Action Button */}
        <div className="flex-shrink-0">
          <button
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded text-sm font-medium transition-colors"
            onClick={handleViewClick}
          >
            View
          </button>
        </div>
      </div>
    </div>
  );
}